import React, { useState } from 'react';

import { Button, Col, LoadingIndicator, MessageBlock, MessageBlockContent, Row, ModalHeader, ModalBody, ModalFooter  } from '@cfa/react-components';
import { IDevice, IDeviceQuery, IUpdateFile } from 'interfaces';
import { DevicesService, parseAxiosError } from 'services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast from "react-hot-toast";

interface IDeviceSearchModalProps {
    updateFile: IUpdateFile,
    payload: any,
    onHide: () => void,
}

export function DeviceSearchModal({ updateFile, payload, onHide }: IDeviceSearchModalProps) {

    const topicOptions = [
        "update/file",
        "update/file/firmware",
        "update/file/menu"
    ];

    // state
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // device search list
    const [devices, setDevices] = useState<IDevice[]>([]);
    const [selectedDevice, setSelectedDevice] = useState<IDevice>(undefined);

    // form search values
    const [location, setLocation] = useState('');

    const onKeypress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onSubmitSearchValues();
        }
    }

    // form submission
    const onSubmitSearchValues = () => {
    
        if (location == "") return;

        setLoading(true);
        setSelectedDevice(undefined);
        setDevices([]);

        let query: IDeviceQuery = {
            manufacturerId: updateFile.manufacturerId,
            modelId: updateFile.deviceModelIds[0],
            locationNumber: location,
        }
    
        DevicesService.searchDevices(query)
            .then((devs) => {
                doSuccess(devs);
            }).catch((err) => {
                // handle 404 composite query
                if (err.response.status == 404) {
                    doSuccess([]);
                    return;
                }
                setLoading(false);
                setErrorMessage(parseAxiosError(err))
            });
    };

    const doSuccess = (devs) => {
        setDevices(devs.devices);
        setErrorMessage("");
        setLoading(false);
    };

    // topic selection
    const [topic, setTopic] = useState(topicOptions[0]);

    // thing id submission
    const onSubmitPublish = () => {
        if (!selectedDevice || topic === "") return;

        setSubmitting(true);

        let data = {
            topicSuffix: topic,
            payload: payload,
            thingId: selectedDevice.thingId,
            location: selectedDevice.location,
            updateFileId: updateFile.id,
            updateTypeId: updateFile.updateTypeId,
            manufacturerId: updateFile.manufacturerId,
            modelId: selectedDevice.modelId,
        };


        DevicesService.publishToDevice(data)
            .then(() => {
                toast.success("Published to " + topic);

                setSubmitting(false);
                onHide();
            })
            .catch(err => {
                toast.success("Error publishing: " + parseAxiosError(err));
                setSubmitting(false);
            })       
    }

    // select
    const isSelected = (thing: string) => selectedDevice && selectedDevice.thingId === thing;

    return (<>

        <ModalHeader>
            Search Devices
        </ModalHeader>

        <ModalBody>
            {errorMessage && (<Row style={{justifyContent: 'center'}}>
                <MessageBlock severity='error'>
                    <MessageBlockContent>{errorMessage.toString()}</MessageBlockContent>
                </MessageBlock>
            </Row>)}
            <Row>
                <Col className="deviceSearch" lg={4} style={{padding: "0px 15px"}}>
                    <div className="deviceSearch_group">
                        <div className="deviceSearch_label">
                            {'Location'}
                        </div>
                        <div className="deviceSearch_value">
                            <div className="inputWithButton">
                                <input
                                    type="text"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    onKeyPress={onKeypress}
                                    maxLength={128}
                                />
                                <button type="button" onClick={onSubmitSearchValues}>
                                    <FontAwesomeIcon icon="angle-right" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <Row>
                        <div className="deviceSearch_label">
                            {'Topic'}
                        </div>
                        <select onChange={(e) => setTopic(e.target.value)} defaultValue="default">
                            {topicOptions.map((t, i) => (
                                <option value={t} key={i}>
                                    {t}
                                </option>
                            ))}
                        </select>
                    </Row>
                </Col>
                <Col>
                    {loading && (<Row style={{justifyContent: 'center'}}>
                        <LoadingIndicator size='md' variant='inline'>
                        </LoadingIndicator>
                    </Row>)}
                    {devices.length > 0 && devices.map((d) => (
                        <li className="devicesItem" key={d.thingId}>
                            <div className="devicesItem_checkCol devicesItem_col">
                                <div className="devicesItem_checkCircle">
                                    <input 
                                        name={d.thingId + ''}
                                        type="checkbox"
                                        className="devicesItem_check" 
                                        onChange={() => setSelectedDevice(d)}
                                        checked={isSelected(d.thingId)}
                                    />
                                </div>
                            </div>
                            <div className="deviceSearchItem_modalLink">
                                <div className="devicesItem_manufacturer ellipsis" title={d.thingId}>
                                    {d.thingId}
                                </div>
                            </div>
                        </li>
                    ))}
                    {devices.length === 0 &&
                        <div className="devicesList_empty">
                            {'No devices found.'}
                        </div>
                    }
                </Col>
            </Row>
        </ModalBody>

        <ModalFooter>
            <Button onClick={onHide}>
                Close
            </Button>
            <Button type="submit" onClick={onSubmitPublish} disabled={!selectedDevice || submitting}>
                {submitting ? 'Publishing...' : 'Publish'}
            </Button>
        </ModalFooter>

    </>);
}
